import { BreedingPoolManager } from './breedingPoolManager'
import { BreedingManager as BreedLogic } from '@/breedLogic'

export class NPCBreedingManager {
  static BREEDING_COOLDOWN_DAYS = 30 // NPCs wait 30 days between breeding attempts
  static MIN_BREEDING_FUNDS = 45000 // 30k breeding + 15k buffer for partner fees
  static BREEDING_DESIRE_THRESHOLD = 0.15 // 15% chance per eligible female per week

  /**
   * Process daily breeding decisions for all NPCs
   * @param {Object} gameStore - Game store instance
   */
  static processDailyBreeding(gameStore) {
    if (!gameStore.owners) return

    // Process each NPC owner
    gameStore.owners.forEach(owner => {
      if (owner.isPlayer) return // Skip player

      this.processOwnerBreeding(owner, gameStore)
    })
  }

  /**
   * Process breeding decisions for a specific NPC owner
   * @param {Object} owner - NPC owner
   * @param {Object} gameStore - Game store instance
   */
  static processOwnerBreeding(owner, gameStore) {
    // Check if owner has sufficient funds
    if (owner.money < this.MIN_BREEDING_FUNDS) return

    // Get owner's chocobos
    const ownerChocobos = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id)
    )

    // Find eligible females for breeding
    const eligibleFemales = ownerChocobos.filter(chocobo => {
      // Must be female, high level, not currently breeding
      if (chocobo.gender !== 'female') return false
      if (chocobo.level < 10) return false // NPCs only breed mature chocobos
      if (chocobo.isBreeding) return false
      
      // Check breeding cooldown
      if (this.hasRecentBreeding(chocobo, gameStore)) return false
      
      return true
    })

    if (eligibleFemales.length === 0) return

    // Check stable capacity (NPCs maintain smaller stables)
    const maxStableSize = this.getMaxStableSize(owner)
    if (ownerChocobos.length >= maxStableSize) return

    // Process each eligible female
    eligibleFemales.forEach(female => {
      // Weekly breeding check (roughly 1/7 chance per day)
      const breedingChance = this.BREEDING_DESIRE_THRESHOLD / 7
      if (Math.random() > breedingChance) return

      this.attemptBreeding(owner, female, gameStore)
    })
  }

  /**
   * Attempt to breed a specific female chocobo
   * @param {Object} owner - NPC owner
   * @param {Object} female - Female chocobo to breed
   * @param {Object} gameStore - Game store instance
   */
  static attemptBreeding(owner, female, gameStore) {
    // Find best available partner
    const partner = this.findBestPartner(owner, female, gameStore)
    if (!partner) return

    // Calculate total breeding cost
    const breedingCost = this.calculateBreedingCost(partner)
    if (owner.money < breedingCost) return

    try {
      // Execute breeding
      const breedingRecord = this.executeNPCBreeding(owner, female, partner, gameStore)
      
      // Deduct costs
      owner.money -= breedingCost
      
      // Pay partner fees if applicable
      this.processPartnerPayment(partner)
      
      // Add transaction record
      this.recordBreedingTransaction(owner, breedingRecord, breedingCost, gameStore)
      
      // NPC breeding completed
      
    } catch (error) {
      console.error('NPC breeding failed:', error)
    }
  }

  /**
   * Find the best breeding partner for a female chocobo
   * @param {Object} owner - NPC owner
   * @param {Object} female - Female chocobo
   * @param {Object} gameStore - Game store instance
   * @returns {Object|null} Best partner or null
   */
  static findBestPartner(owner, female, gameStore) {
    const candidates = []

    // 1. Check own stable for males
    const ownMales = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id) && 
      c.gender === 'male' && 
      c.level >= 8 && 
      !c.isBreeding
    )

    ownMales.forEach(male => {
      candidates.push({
        chocobo: male,
        cost: 0,
        source: 'own',
        score: this.calculatePartnerScore(female, male)
      })
    })

    // 2. Check other NPCs' males (if no good own partners)
    if (candidates.length === 0 || candidates[0].score < 0.6) {
      const otherNPCMales = this.findAvailableNPCMales(owner, gameStore)
      otherNPCMales.forEach(({ male, ownerOfMale, cost }) => {
        candidates.push({
          chocobo: male,
          cost: cost,
          source: 'npc',
          ownerOfMale: ownerOfMale,
          score: this.calculatePartnerScore(female, male)
        })
      })
    }

    // 3. Check retired breeding pool as last resort
    if (candidates.length === 0 || candidates[0].score < 0.4) {
      const retiredPartners = BreedingPoolManager.getAvailablePartners(female, gameStore)
      retiredPartners.slice(0, 5).forEach(retired => { // Top 5 retired partners
        if (owner.money >= retired.breedingPrice + 30000) {
          candidates.push({
            chocobo: retired,
            cost: retired.breedingPrice,
            source: 'retired',
            score: this.calculatePartnerScore(female, retired)
          })
        }
      })
    }

    // Sort by score and affordability
    candidates.sort((a, b) => {
      // Prioritize affordability, then score
      const costDiff = a.cost - b.cost
      if (costDiff > 10000) return costDiff // Significant cost difference
      return b.score - a.score // Higher score better
    })

    return candidates.length > 0 ? candidates[0] : null
  }

  /**
   * Find available males from other NPC stables
   * @param {Object} requestingOwner - Owner looking for partner
   * @param {Object} gameStore - Game store instance
   * @returns {Array} Available males with costs
   */
  static findAvailableNPCMales(requestingOwner, gameStore) {
    const available = []

    gameStore.owners.forEach(owner => {
      if (owner.isPlayer || owner.id === requestingOwner.id) return

      const ownerMales = gameStore.chocobos.filter(c => 
        owner.chocoboIds.includes(c.id) && 
        c.gender === 'male' && 
        c.level >= 10 && 
        !c.isBreeding
      )

      ownerMales.forEach(male => {
        // Calculate stud fee based on male's quality
        const studFee = this.calculateStudFee(male)
        
        // NPCs are willing to rent out their males for income
        if (Math.random() < 0.7) { // 70% chance of agreement
          available.push({
            male: male,
            ownerOfMale: owner,
            cost: studFee
          })
        }
      })
    })

    return available
  }

  /**
   * Calculate breeding partner compatibility score
   * @param {Object} female - Female chocobo
   * @param {Object} male - Male chocobo
   * @returns {number} Compatibility score (0-1)
   */
  static calculatePartnerScore(female, male) {
    let score = 0

    // Level compatibility (25%)
    const avgLevel = (female.level + male.level) / 2
    score += Math.min(avgLevel / 40, 1) * 0.25

    // Stat compatibility (50%)
    const statAvg = (female.speed + female.stamina + male.speed + male.stamina) / 4
    score += Math.min(statAvg / 120, 1) * 0.5

    // X-Factor potential (15%)
    if (female.hasSpeedXFactor || male.hasSpeedXFactor) score += 0.075
    if (female.hasStaminaXFactor || male.hasStaminaXFactor) score += 0.075

    // Color combination bonus (10%)
    if (this.hasSpecialColorCombination(female, male)) {
      score += 0.1
    } else if (female.color === male.color) {
      score += 0.05 // Pure breeding bonus
    }

    return Math.min(score, 1)
  }

  /**
   * Check for special color combinations
   * @param {Object} female - Female chocobo
   * @param {Object} male - Male chocobo
   * @returns {boolean} Has special combination
   */
  static hasSpecialColorCombination(female, male) {
    // Golden chocobo chance
    return (female.color === 'black' && male.color === 'yellow') ||
           (female.color === 'yellow' && male.color === 'black')
  }

  /**
   * Calculate stud fee for a male chocobo
   * @param {Object} male - Male chocobo
   * @returns {number} Stud fee
   */
  static calculateStudFee(male) {
    let baseFee = 8000

    // Level multiplier
    baseFee += (male.level - 10) * 500

    // Stat multiplier
    const totalStats = male.speed + male.stamina
    baseFee += Math.max(0, (totalStats - 100)) * 100

    // X-Factor bonus
    if (male.hasSpeedXFactor) baseFee += 5000
    if (male.hasStaminaXFactor) baseFee += 5000

    // Color rarity
    const colorMultipliers = {
      yellow: 1.0, blue: 1.2, green: 1.2, pink: 1.3,
      black: 1.4, white: 1.8, red: 1.8, gold: 3.0
    }
    baseFee *= (colorMultipliers[male.color] || 1.0)

    return Math.round(baseFee)
  }

  /**
   * Calculate total breeding cost
   * @param {Object} partner - Partner info
   * @returns {number} Total cost
   */
  static calculateBreedingCost(partner) {
    return 30000 + (partner.cost || 0)
  }

  /**
   * Execute NPC breeding
   * @param {Object} owner - NPC owner
   * @param {Object} female - Female chocobo
   * @param {Object} partner - Partner info
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Breeding record
   */
  static executeNPCBreeding(owner, female, partner, gameStore) {
    // Use the main breeding manager but adapt for NPCs
    const male = partner.chocobo
    
    // Pre-generate offspring
    const offspringData = BreedLogic.breedChocobos(female, male, gameStore.currentDay + 14)
    
    // Set lineage information for the offspring
    offspringData.setLineage(female, male)
    
    // Create NPC breeding record
    const breedingRecord = {
      id: crypto.randomUUID(),
      motherId: female.id,
      motherName: female.name,
      fatherId: male.id,
      fatherName: male.name,
      ownerId: owner.id,
      ownerName: owner.name,
      startDay: gameStore.currentDay,
      endDay: gameStore.currentDay + 14,
      stage: 'incubating',
      offspringData: offspringData.toJSON(),
      paid: this.calculateBreedingCost(partner),
      isNPC: true
    }

    // Mark chocobos as breeding
    female.startBreeding(male.id, male.name, gameStore.currentDay, 'mother')
    // Note: Males don't get marked as unavailable in breeding system
    
    // Add to active breedings
    gameStore.activeBreedings.push(breedingRecord)
    
    return breedingRecord
  }

  /**
   * Process payment to partner owner
   * @param {Object} partner - Partner info
   */
  static processPartnerPayment(partner) {
    if (partner.source === 'npc' && partner.ownerOfMale) {
      partner.ownerOfMale.money += partner.cost
      
      // Track stud income
      if (!partner.ownerOfMale.studIncome) {
        partner.ownerOfMale.studIncome = 0
      }
      partner.ownerOfMale.studIncome += partner.cost
    }
    // Retired breeding pool payments handled by BreedingPoolManager
  }

  /**
   * Record breeding transaction for economy tracking
   * @param {Object} owner - NPC owner
   * @param {Object} breedingRecord - Breeding record
   * @param {number} cost - Total cost
   * @param {Object} gameStore - Game store instance
   */
  static recordBreedingTransaction(owner, breedingRecord, cost, gameStore) {
    if (!gameStore.npcBreedingTransactions) {
      gameStore.npcBreedingTransactions = []
    }

    gameStore.npcBreedingTransactions.unshift({
      id: crypto.randomUUID(),
      ownerId: owner.id,
      ownerName: owner.name,
      breedingId: breedingRecord.id,
      cost: cost,
      day: gameStore.currentDay,
      motherName: breedingRecord.motherName,
      fatherName: breedingRecord.fatherName
    })

    // Keep only last 100 transactions
    if (gameStore.npcBreedingTransactions.length > 100) {
      gameStore.npcBreedingTransactions = gameStore.npcBreedingTransactions.slice(0, 100)
    }
  }

  /**
   * Check if chocobo has bred recently
   * @param {Object} chocobo - Chocobo to check
   * @param {Object} gameStore - Game store instance
   * @returns {boolean} Has recent breeding
   */
  static hasRecentBreeding(chocobo, gameStore) {
    if (!gameStore.completedBreedings) return false

    const recentBreeding = gameStore.completedBreedings.find(breeding => 
      breeding.motherId === chocobo.id && 
      (gameStore.currentDay - breeding.completedDay) < this.BREEDING_COOLDOWN_DAYS
    )

    return !!recentBreeding
  }

  /**
   * Get maximum stable size for NPC owner
   * @param {Object} owner - NPC owner
   * @returns {number} Max stable size
   */
  static getMaxStableSize(owner) {
    // NPCs maintain smaller stables than player
    // Wealthy NPCs can afford larger stables
    if (owner.money > 200000) return 12
    if (owner.money > 100000) return 10
    if (owner.money > 50000) return 8
    return 6
  }

  /**
   * Get NPC breeding statistics
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Statistics
   */
  static getNPCBreedingStats(gameStore) {
    const npcBreedings = gameStore.activeBreedings?.filter(b => b.isNPC) || []
    const npcTransactions = gameStore.npcBreedingTransactions || []

    return {
      activeNPCBreedings: npcBreedings.length,
      totalNPCBreedings: npcTransactions.length,
      averageBreedingCost: npcTransactions.length > 0 
        ? npcTransactions.reduce((sum, t) => sum + t.cost, 0) / npcTransactions.length 
        : 0,
      mostActiveBreeder: this.getMostActiveBreeder(npcTransactions),
      totalStudFees: this.getTotalStudFees(gameStore)
    }
  }

  /**
   * Get most active NPC breeder
   * @param {Array} transactions - Breeding transactions
   * @returns {Object|null} Most active breeder
   */
  static getMostActiveBreeder(transactions) {
    if (transactions.length === 0) return null

    const breedingCounts = {}
    transactions.forEach(t => {
      breedingCounts[t.ownerName] = (breedingCounts[t.ownerName] || 0) + 1
    })

    const topBreeder = Object.entries(breedingCounts)
      .sort(([,a], [,b]) => b - a)[0]

    return topBreeder ? { name: topBreeder[0], count: topBreeder[1] } : null
  }

  /**
   * Get total stud fees paid in economy
   * @param {Object} gameStore - Game store instance
   * @returns {number} Total stud fees
   */
  static getTotalStudFees(gameStore) {
    return gameStore.owners.reduce((total, owner) => {
      return total + (owner.studIncome || 0)
    }, 0)
  }
}